/**
 * Avatar options for agents.
 * Each avatar has a unique value identifier (used for storage), a background color
 * (Vuetify color class), and an MDI icon. Avatars 1-6 are feminine, 7-12 are masculine.
 * @type {Array<{ value: string, color: string, icon: string }>}
 */
const agentAvatars = [
  {
    value: 'avatar-1',
    color: 'purple lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-2',
    color: 'pink lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-3',
    color: 'teal lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-4',
    color: 'orange lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-5',
    color: 'indigo lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-6',
    color: 'red lighten-1',
    icon: 'mdi-face-woman-outline'
  },
  {
    value: 'avatar-7',
    color: 'blue lighten-1',
    icon: 'mdi-face-man-outline'
  },
  {
    value: 'avatar-8',
    color: 'green lighten-1',
    icon: 'mdi-face-man-outline'
  },
  {
    value: 'avatar-9',
    color: 'brown lighten-1',
    icon: 'mdi-face-man-outline'
  },
  {
    value: 'avatar-10',
    color: 'cyan lighten-1',
    icon: 'mdi-face-man-outline'
  },
  {
    value: 'avatar-11',
    color: 'lime darken-1',
    icon: 'mdi-face-man-outline'
  },
  {
    value: 'avatar-12',
    color: 'blue-grey lighten-1',
    icon: 'mdi-face-man-outline'
  }
]

/**
 * Suggested human-like names for agents.
 * Used to generate a random name suggestion in the creation wizard.
 * @type {string[]}
 */
const agentSuggestedNames = [
  'Max',
  'Valentina',
  'Andres',
  'Sofia',
  'Camilo',
  'Isabela',
  'Felipe',
  'Catalina'
]

/**
 * Task catalog for collections agents (dunning, payment retries, customer outreach).
 * Mirrors the backend catalog registered under kind='collections' in agents/catalog.py.
 * Each task includes a Spanish title and description for display in the UI.
 * @type {Array<{ task_key: string, title: string, description: string, recommended_autonomy: string, default_enabled: boolean }>}
 */
const agentCollectionsTasks = [
  {
    task_key: 'smart_retry_timing',
    title: 'Reintento inteligente de pagos',
    description: 'Detecta el momento con mayor probabilidad de éxito para reintentar cada pago fallido.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'contact_timing_cadence',
    title: 'Cadencia de contacto con el cliente',
    description: 'Define cuándo contactar al cliente, cuándo esperar y cuándo evitar un mensaje para no saturarlo.',
    recommended_autonomy: 'approval_required',
    default_enabled: true
  },
  {
    task_key: 'cash_impact_prioritization',
    title: 'Priorización de cuentas por cobrar',
    description: 'Entrega listas de clientes con deuda priorizadas según el impacto que pueden generar en la caja.',
    recommended_autonomy: 'suggest_only',
    default_enabled: true
    // Priorización por Impacto
    // Genera listas de trabajo ordenadas por impacto esperado en caja.
  },
  {
    task_key: 'escalation_orchestrator',
    title: 'Escalamiento de casos de riesgo',
    description: 'Deriva al equipo los casos bloqueados o de alto riesgo cuando superan los límites definidos.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'non_payment_cause_triage',
    title: 'Diagnóstico de causas de no pago',
    description: 'Identifica por qué el cliente no ha pagado, deriva cada caso al flujo correcto y pausa la cobranza si corresponde.',
    recommended_autonomy: 'approval_required',
    default_enabled: false
  },
  {
    task_key: 'promise_to_pay_tracker',
    title: 'Seguimiento de compromisos de pago',
    description: 'Registra las fechas en que el cliente se compromete a pagar y agenda el seguimiento de cada promesa.',
    recommended_autonomy: 'suggest_only',
    default_enabled: false
  },
  {
    task_key: 'channel_strategy_selector',
    title: 'Define el mejor canal de contacto',
    description: 'Elige el canal de contacto óptimo según el historial de acciones, respuestas y restricciones del cliente.',
    recommended_autonomy: 'approval_required',
    default_enabled: false
    // Selector de Canal
    // Elige el canal de contacto según el historial de respuestas y restricciones.
  },
  {
    task_key: 'message_composer',
    title: 'Redacción de mensajes de cobranza',
    description: 'Prepara borradores de mensajes para el cliente, listos para que el equipo los revise y apruebe.',
    recommended_autonomy: 'suggest_only',
    default_enabled: false
  },
  {
    task_key: 'decision_explanations',
    title: 'Explicación de cada decisión',
    description: 'Acompaña cada acción del agente con una justificación breve y clara de por qué la tomó.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'daily_digest_sla_monitor',
    title: 'Resumen diario y alertas de gestión',
    description: 'Envía al equipo un resumen diario del avance y avisa cuando un caso supera el tiempo de gestión esperado.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  }
]

/**
 * Task catalog for billing agents (billing cycle readiness, OC requests, reference management).
 * Mirrors the backend catalog registered under kind='billing' in agents/catalog.py.
 * Each task includes a Spanish title and description for display in the UI.
 * @type {Array<{ task_key: string, title: string, description: string, recommended_autonomy: string, default_enabled: boolean }>}
 */
const agentBillingTasks = [
  {
    task_key: 'billing_readiness_check',
    title: 'Verificación de Facturación',
    description: 'Revisión previa a cada ciclo — detecta bloqueos (referencias faltantes, tarjetas por vencer) antes de facturar.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'oc_request_timing',
    title: 'Solicitud de OC',
    description: 'Decide cuándo y cómo pedir una Orden de Compra al cliente antes de la fecha de facturación.',
    recommended_autonomy: 'approval_required',
    default_enabled: true
  },
  {
    task_key: 'reference_expiry_management',
    title: 'Vencimiento de Referencias',
    description: 'Alerta cuando una referencia o OC está por vencer y define la urgencia según los días restantes.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'usage_record_validation',
    title: 'Validación de Registros de Uso',
    description: 'Alerta si faltan registros de consumo antes del cierre de ciclo en planes de uso medido.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  },
  {
    task_key: 'ach_match_monitor',
    title: 'Monitor de Transferencias',
    description: 'Detecta transferencias sin conciliar que podrían corresponder a facturas pendientes.',
    recommended_autonomy: 'suggest_only',
    default_enabled: true
  },
  {
    task_key: 'billing_block_escalation',
    title: 'Escalada de Bloqueos',
    description: 'Escala bloqueos de facturación no resueltos cuando se acerca el cierre del ciclo.',
    recommended_autonomy: 'auto_execute',
    default_enabled: true
  }
]

/**
 * Combined task list used for scratch agents (kind='custom').
 * Merges all collections and billing tasks into a single flat list,
 * with none pre-selected, so the user can freely pick any combination.
 * @type {Array<{ task_key: string, title: string, description: string, recommended_autonomy: string, default_enabled: boolean, catalog: string }>}
 */
const agentAllTasks = [
  ...agentCollectionsTasks.map(t => ({ ...t, catalog: 'collections' })),
  ...agentBillingTasks.map(t => ({ ...t, catalog: 'billing' }))
]

/**
 * Severity styling for insights and inbox items.
 * Maps the API severity (info | warning | critical) to a Vuetify chip color,
 * text color, label and icon. Approvals in the inbox reuse warning/critical.
 * @type {Object<string, { label: string, color: string, textColor: string, icon: string }>}
 */
const agentInsightSeverity = {
  info: { label: 'Informativo', color: 'blue lighten-5', textColor: 'blue darken-2', icon: 'mdi-information-outline' },
  warning: { label: 'Atención', color: 'amber lighten-5', textColor: 'amber darken-4', icon: 'mdi-alert-outline' },
  critical: { label: 'Crítico', color: 'red lighten-5', textColor: 'red darken-2', icon: 'mdi-alert-circle-outline' }
}

/**
 * Human-readable labels for each insight_type.
 * @type {Object<string, string>}
 */
const agentInsightTypeLabel = {
  list: 'Lista priorizada',
  report: 'Reporte',
  alert: 'Alerta',
  explanation: 'Explicación',
  signal: 'Señal',
  readiness: 'Verificación',
  triage: 'Triage',
  template: 'Plantilla',
  strategy: 'Estrategia'
}

/**
 * Labels for insight resolution status.
 * @type {Object<string, string>}
 */
const agentInsightStatusLabel = {
  new: 'Nuevo',
  acknowledged: 'Reconocido',
  dismissed: 'Descartado',
  expired: 'Expirado'
}

/**
 * Labels for the inbox item source.
 * @type {Object<string, string>}
 */
const agentInboxSourceLabel = {
  approval: 'Aprobación',
  insight: 'Insight'
}

/**
 * Display metadata for audit-event types (timeline trace). Includes the two new
 * insight events (agent_insight_recorded, agent_insight_acknowledged). Ready for
 * a future timeline; not rendered yet.
 * @type {Object<string, { label: string, icon: string }>}
 */
const agentAuditEventLabel = {
  agent_decision_recorded: { label: 'Decisión registrada', icon: 'mdi-lightbulb-outline' },
  agent_action_queued: { label: 'Acción encolada', icon: 'mdi-tray-plus' },
  agent_action_enqueued: { label: 'Acción enviada a ejecución', icon: 'mdi-send-outline' },
  agent_action_scheduled: { label: 'Acción programada', icon: 'mdi-calendar-clock' },
  agent_action_queue_failed: { label: 'Fallo al encolar', icon: 'mdi-alert-outline' },
  agent_action_executed: { label: 'Acción ejecutada', icon: 'mdi-check-circle-outline' },
  agent_action_failed: { label: 'Acción fallida', icon: 'mdi-close-circle-outline' },
  agent_approval_resolved: { label: 'Aprobación resuelta', icon: 'mdi-gavel' },
  agent_insight_recorded: { label: 'Insight registrado', icon: 'mdi-eye-outline' },
  agent_insight_acknowledged: { label: 'Insight reconocido', icon: 'mdi-check-decagram-outline' }
}

export {
  agentAvatars,
  agentSuggestedNames,
  agentCollectionsTasks,
  agentBillingTasks,
  agentAllTasks,
  agentInsightSeverity,
  agentInsightTypeLabel,
  agentInsightStatusLabel,
  agentInboxSourceLabel,
  agentAuditEventLabel
}
